import React from "react";
import CloudFile404 from "../CloudFile404";
import ShareScene from "./ShareScene";
import { useShareFile } from "./useShareFile";
import { getShareFileIdFromPath } from "../../data/shareRouting";
import { fileEditorPath } from "../../data/cloudRouting";

/**
 * Standalone read-only viewer for `/share/<id>`. Light chrome over the inert
 * `ShareScene` canvas:
 *   - file title
 *   - "Create your own" (→ app root)
 *   - an "Edit" link to `/f/<id>` for the owner ONLY.
 *
 * Access rules are enforced by the data layer, not this component: a private /
 * deleted / nonexistent / malformed id resolves to the SAME uniform 404 as
 * everywhere else (no redirect, no existence leak). The owner-only Edit button
 * is the single place owner-ness changes what is shown, and it is a link — it
 * grants no editing here; `/f/<id>` re-checks ownership server-side.
 */
export const ShareView: React.FC = () => {
  const fileId = getShareFileIdFromPath();
  const { status, file, scene, isOwner } = useShareFile(fileId);

  if (status === "loading") {
    return (
      <div
        data-testid="share-view-loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
        }}
      >
        Loading…
      </div>
    );
  }

  if (status === "notfound" || !file || !scene) {
    return <CloudFile404 />;
  }

  return (
    <div
      data-testid="share-view"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.5rem 1rem",
          borderBottom: "1px solid var(--default-border-color, #e5e5ea)",
          background: "var(--island-bg-color, #fff)",
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: "0.95rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "var(--text-primary-color, #1b1b1f)",
          }}
          title={file.title}
        >
          {file.title}
        </span>
        <span style={{ flex: 1 }} />
        {isOwner && (
          <a
            href={fileEditorPath(file.id)}
            style={{
              padding: "0.35rem 0.85rem",
              borderRadius: "0.5rem",
              border: "1px solid var(--default-border-color, #e5e5ea)",
              color: "var(--text-primary-color, #1b1b1f)",
              fontWeight: 600,
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            Edit
          </a>
        )}
        <a
          href="/"
          style={{
            color: "#6965db",
            fontWeight: 600,
            fontSize: "0.85rem",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          {/* Not "Open in Mindmap.so": this map is already open in Mindmap.so,
              and the link goes to the app root, not to this file. Opening THIS
              file is what the owner's Edit link beside it does. */}
          Create your own
        </a>
      </header>
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <ShareScene fileId={file.id} scene={scene} fitToContentOnLoad />
      </div>
    </div>
  );
};

export default ShareView;
